import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { ReportCard } from '@components/Pages/AnalyticsPage/ReportCard';
import { MultiSelect } from '@components/ui/MultiSelect/MultiSelect';
import { DateRangeField, type IDateRange } from '@components/ui/DateRangeField/DateRangeField';
import { fetchTags } from '@apis/workerTimesheet';
import type { IGroupedOptions } from '@components/Pages/AnalyticsPage/analytics.types';
import { tagsToGroupedOptions } from '@components/Pages/AnalyticsPage/Reports/WorkerTimesheet/selectors';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ } from '@utils/dateUtils';
import { CardIcon } from '../CardIcon';
import { todayCap } from '../utils/dateRules';

export interface IWorkerTimesheetSubmission {
  tagIds: string[];
  range: IDateRange;
}

interface IWorkerTimesheetCardProps {
  isSelected: boolean;
  onSelect: (name: 'workersTimesheet') => void;
  onSubmit: (payload: IWorkerTimesheetSubmission) => void;
}

/**
 * Worker Timesheet analytics card. Wires the picker dropdown to the real
 * `/api/sites/:siteId/analytics/tags` endpoint via TanStack Query — the
 * legacy `MOCK_TAGS_GROUPED_BY_TRADE` import is gone. The card still owns
 * the picker UI state (tag selection + date range); submission propagates
 * through to the parent `AnalyticsPage` which decides what to do with it.
 *
 * `enabled: !!siteId` keeps the query inert until the route param is
 * available (the card is mounted under `/dashboard/:siteId/analytics`, so
 * this only matters during the brief render before route params settle).
 * The 5-minute `staleTime` mirrors the cadence used by the other analytics
 * pickers — tag rosters change infrequently within a single session.
 */
export function WorkerTimesheetCard({ isSelected, onSelect, onSubmit }: IWorkerTimesheetCardProps) {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [range, setRange] = useState<IDateRange>({ from: null, to: null });

  const tagsQuery = useQuery({
    queryKey: ['analyticsTags', siteId] as const,
    queryFn: ({ signal }) => fetchTags(siteId!, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });

  const groups = useMemo<IGroupedOptions[]>(
    () => tagsToGroupedOptions(tagsQuery.data ?? []),
    [tagsQuery.data],
  );

  const tagsLoading = tagsQuery.isLoading;
  const tagsEmpty = !tagsLoading && groups.length === 0;
  const canSubmit =
    !tagsLoading &&
    tagIds.length > 0 &&
    range.from !== null &&
    range.to !== null;

  const placeholder = tagsLoading
    ? t('analytics.filters.loading')
    : tagsEmpty
      ? t('analytics.filters.noTagsFound')
      : t('analytics.filters.selectPlaceholder');

  return (
    <ReportCard
      name="workersTimesheet"
      title={t('analytics.cards.workersTimesheet.title')}
      description={t('analytics.cards.workersTimesheet.description')}
      icon={<CardIcon name="clock" />}
      isSelected={isSelected}
      onSelect={() => onSelect('workersTimesheet')}
      onSubmit={() => onSubmit({ tagIds, range })}
      submitLabel={t('analytics.createButton')}
      submitDisabled={!canSubmit}
    >
      <MultiSelect
        value={tagIds}
        onChange={setTagIds}
        groups={groups}
        placeholder={placeholder}
        ariaLabel={t('analytics.filters.tags')}
        disabled={tagsLoading || tagsEmpty}
      />
      <DateRangeField
        value={range}
        onChange={setRange}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={t('analytics.filters.datePeriod')}
        maxDate={todayCap(tz)}
      />
    </ReportCard>
  );
}
